import axiosInstance from '../../services/axiosInstance';
import { BarcodeOptions, GenerateResult, BarcodeListItem, ProductLookupResult, BatchGenerateResponse } from './types';

export async function validateEan(ean: string): Promise<{
  valid: boolean;
  ean?: string;
  checkDigit?: number;
  error?: string;
  correctedEan?: string;
  suggestedCheckDigit?: number;
}> {
  const { data } = await axiosInstance.get(`/api/barcode/validate/${encodeURIComponent(ean)}`);
  return data;
}

export async function getCheckDigit(first12: string): Promise<{ checkDigit: number; fullEan: string }> {
  const { data } = await axiosInstance.get(`/api/barcode/check-digit/${encodeURIComponent(first12)}`);
  return data;
}

export async function getPreviewSvg(ean: string, options: Partial<BarcodeOptions> = {}): Promise<string> {
  const { data } = await axiosInstance.post('/api/barcode/preview', { ean, ...options });
  return data.svg;
}

export async function lookupProduct(artikelnummer: string): Promise<ProductLookupResult> {
  const { data } = await axiosInstance.get(`/api/barcode/lookup-product/${encodeURIComponent(artikelnummer)}`);
  return data;
}

export async function generateBarcode(ean: string, options: Partial<BarcodeOptions> = {}, artikelnummer?: string, categoryIds?: string[]): Promise<GenerateResult> {
  const { data } = await axiosInstance.post('/api/barcode/generate', { ean, ...options, artikelnummer, categoryIds });
  return data;
}

export async function batchGenerateBarcodes(body: {
  assetIds: string[];
  options: Partial<BarcodeOptions>;
  categoryIds?: string[];
  dryRun?: boolean;
  enqueueAi?: boolean;
}): Promise<BatchGenerateResponse> {
  const { data } = await axiosInstance.post('/api/barcode/batch-generate', body);
  return data;
}

export async function getPreviewSvgPrecise(ean: string, options: Partial<BarcodeOptions> = {}): Promise<{ svg: string; dimensions: any }> {
  const { data } = await axiosInstance.post('/api/barcode/preview-precise', { ean, ...options });
  return data;
}

export async function generateBarcodePrecise(ean: string, options: Partial<BarcodeOptions> = {}, artikelnummer?: string): Promise<GenerateResult> {
  const { data } = await axiosInstance.post('/api/barcode/generate-precise', { ean, ...options, artikelnummer });
  return data;
}

export async function downloadBarcode(assetId: string): Promise<void> {
  const response = await axiosInstance.get(`/api/barcode/download/${assetId}`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'image/tiff' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const contentDisposition = response.headers['content-disposition'];
  const match = contentDisposition?.match(/filename="(.+)"/);
  a.download = match?.[1] || `ean13-barcode.tiff`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadBarcodePng(assetId: string): Promise<void> {
  const response = await axiosInstance.get(`/api/barcode/download-png/${assetId}`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'image/png' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const contentDisposition = response.headers['content-disposition'];
  const match = contentDisposition?.match(/filename="(.+)"/);
  a.download = match?.[1] || `ean13-barcode.png`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function listBarcodes(): Promise<BarcodeListItem[]> {
  const { data } = await axiosInstance.get('/api/barcode/list');
  return data.assets;
}

export async function deleteBarcode(assetId: string): Promise<void> {
  await axiosInstance.delete(`/api/assets/${assetId}`);
}
